import { DataProvider, fetchUtils } from 'react-admin';
import { withRefreshRetry } from './services/httpRefreshHandler';

const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
const mainApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ssoApiUrl = `${ssoUrl}/api`;

// Resources that are in the main API (online-academy-api) instead of SSO
const mainApiResources = [
    'learning_paths',
    'learning_path_courses',
    'learning_path_enrollments',
    'courses',
    'lessons',
    'course_enrollments',
    'course_catalogs',
    'learning_groups',
    'learning_group_members',
    'peer_review_assignments',
    'peer_reviews',
    'badges',
    'user_badges',
    'user_streaks',
    'certificates',
    'media_files',
    'wiki_articles',
    'wiki_categories',
    'course_assignments',
    'document_templates',
];

// Get the correct API URL based on resource
const getApiUrl = (resource: string): string => {
    if (mainApiResources.includes(resource)) {
        // mainApiUrl is base URL like http://localhost:8000
        // We need to return http://localhost:8000/api (without trailing resource path)
        const baseUrl = mainApiUrl.replace(/\/api\/?$/, ''); // Remove /api if present
        return `${baseUrl}/api`;
    }
    return ssoApiUrl;
};

// #368: API Platform legt Fehlermeldungen in hydra:description/detail ab,
// react-admin liest nur error.message — ohne Mapping bleibt der generische
// "Serverfehler"-Toast.
export const extractApiErrorMessage = (body: any): string | null =>
    body?.['hydra:description'] || body?.detail || null;

// Innerer Client — setzt Auth + Content-Type, ruft fetchUtils.fetchJson.
// Wird vom Refresh-Retry-Wrapper (#297) umschlossen, damit bei 401 ein
// Refresh-Versuch laeuft bevor react-admin den User ausloggt.
const baseHttpClient = (url: string, options: any = {}) => {
    const token = localStorage.getItem('token');
    if (!options.headers) {
        options.headers = new Headers({
            Accept: 'application/ld+json',
        });
    }
    if (token) {
        options.headers.set('Authorization', `Bearer ${token}`);
    }
    // Set Content-Type based on method
    if (options.method === 'POST') {
        options.headers.set('Content-Type', 'application/ld+json');
    } else if (options.method === 'PATCH') {
        options.headers.set('Content-Type', 'application/merge-patch+json');
    } else if (options.method === 'PUT') {
        options.headers.set('Content-Type', 'application/ld+json');
    }
    return fetchUtils.fetchJson(url, options).catch((err: any) => {
        const message = extractApiErrorMessage(err?.body);
        if (message) {
            err.message = message;
        }
        throw err;
    });
};

const httpClient = withRefreshRetry(baseHttpClient);

// Convert IRI to ID (e.g., "/api/companies/1" => 1)
export const iriToId = (iri: string): number | string => {
    if (typeof iri === 'number') return iri;
    if (!iri || typeof iri !== 'string') return iri;
    const parts = iri.split('/');
    return parts[parts.length - 1];
};

// Convert ID to IRI (e.g., 1 => "/api/companies/1")
export const idToIri = (resource: string, id: number | string): string => {
    if (typeof id === 'string' && id.startsWith('/api/')) return id;
    return `/api/${resource}/${id}`;
};

// Resolve a single-reference value (id | IRI | embedded {@id|id}) to an IRI, or null.
const refToIri = (resource: string, value: any): string | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object') {
        if (value['@id']) return value['@id'];
        if (value.id !== undefined && value.id !== null) return idToIri(resource, value.id);
        return null;
    }
    if (String(value).startsWith('/api/')) return value as string;
    return idToIri(resource, value);
};

// Fields that should keep only IDs (for ReferenceArrayInput/SelectArrayInput)
const referenceArrayFields = ['userRoles', 'userGroups', 'permissions', 'roles'];

// Single reference fields that should be reduced to ID only (for ReferenceInput)
const singleReferenceFields = ['company', 'department', 'product', 'license', 'invitedBy', 'registeredUser'];

// Fields that should keep full embedded objects (for custom display)
const embeddedArrayFields = ['courses', 'lessons', 'enrollments'];

// Embedded collections that ARE writable and must survive the read-only-collection
// strip below. They are sent back on PATCH/POST and handled explicitly afterwards.
const writableEmbeddedCollections = ['reminders'];

// Check if an object is a simple reference (only has @id, @type, id, and maybe a few basic fields)
const isSimpleReference = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object') return false;
    const keys = Object.keys(obj).filter(k => !k.startsWith('@'));
    // Simple reference typically only has id and maybe name
    return keys.length <= 2 && keys.every(k => ['id', 'name'].includes(k));
};

// Convert API Platform format to React Admin format
export const normalizeData = (data: any): any => {
    if (!data || typeof data !== 'object') return data;

    const normalized: any = { ...data };

    // Extract ID from @id if present
    if (normalized['@id']) {
        normalized.id = iriToId(normalized['@id']);
    }

    // Convert all IRI references to IDs
    for (const key in normalized) {
        const value = normalized[key];

        // Skip @-prefixed keys
        if (key.startsWith('@')) continue;

        // Handle IRI strings (simple references)
        if (typeof value === 'string' && value.startsWith('/api/')) {
            normalized[key] = iriToId(value);
        }
        // Handle embedded objects with @id
        else if (value && typeof value === 'object' && !Array.isArray(value)) {
            if (value['@id']) {
                // For single reference fields (used in ReferenceInput), extract just the ID
                if (singleReferenceFields.includes(key)) {
                    normalized[key] = iriToId(value['@id']);
                } else {
                    // For other embedded resources, normalize recursively to keep all data
                    normalized[key] = normalizeData(value);
                }
            }
        }
        // Handle arrays
        else if (Array.isArray(value)) {
            // Check if this is a reference array field (should be IDs only)
            if (referenceArrayFields.includes(key)) {
                normalized[key] = value.map((item: any) => {
                    if (typeof item === 'string' && item.startsWith('/api/')) {
                        return iriToId(item);
                    }
                    if (item && typeof item === 'object' && item['@id']) {
                        return iriToId(item['@id']);
                    }
                    if (item && typeof item === 'object' && item.id) {
                        return item.id;
                    }
                    return item;
                });
            }
            // For embedded array fields or unknown fields, keep full objects
            else {
                normalized[key] = value.map((item: any) => {
                    // Simple IRI reference in array
                    if (typeof item === 'string' && item.startsWith('/api/')) {
                        return iriToId(item);
                    }
                    // Embedded object in array - normalize recursively to keep all data
                    if (item && typeof item === 'object') {
                        return normalizeData(item);
                    }
                    return item;
                });
            }
        }
    }

    return normalized;
};

// Convert React Admin format to API Platform format
export const denormalizeData = (resource: string, data: any): any => {
    if (!data || typeof data !== 'object') return data;

    const denormalized: any = { ...data };

    // Remove read-only and computed fields that shouldn't be sent on update
    const readOnlyFields = [
        'id', 'uuid', '@id', '@type', '@context',
        'createdAt', 'updatedAt', 'createdBy', 'companyId',
        'courseCount', 'requiredCourseCount', 'enrollmentCount',
        'userCount', 'lessonCount', 'attemptCount', 'progressPercentage',
    ];
    readOnlyFields.forEach(field => delete denormalized[field]);

    // Remove embedded collections (arrays of objects) — these are read-only relations
    // that the API serializes for display but shouldn't be sent back in PATCH
    for (const key in denormalized) {
        if (writableEmbeddedCollections.includes(key)) continue;
        const value = denormalized[key];
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null && !Array.isArray(value[0])) {
            // Check if these are embedded objects (have @id or id) vs simple value arrays
            if (value[0]['@id'] || value[0]['id'] !== undefined) {
                delete denormalized[key];
            }
        }
    }

    // Cast known integer fields (form state may return strings)
    const integerFields = ['userId', 'assignedBy', 'companyId', 'daysBeforeDue'];
    integerFields.forEach(field => {
        if (denormalized[field] !== undefined && denormalized[field] !== null && denormalized[field] !== '') {
            denormalized[field] = Number(denormalized[field]);
        }
    });

    // Convert reference IDs back to IRIs
    const referenceFields: Record<string, string> = {
        company: 'companies',
        parentCompany: 'companies',
        product: 'products',
        user: 'users',
        role: 'roles',
        permission: 'permissions',
        license: 'licenses',
        userRole: 'roles',
        department: 'departments',
        userGroup: 'user_groups',
        course: 'courses',
        emailTemplate: 'document_templates',
        assignment: 'course_assignments',
        catalog: 'course_catalogs',
        lesson: 'lessons',
    };

    for (const key in denormalized) {
        if (writableEmbeddedCollections.includes(key)) continue; // handled explicitly below
        const value = denormalized[key];

        // Single reference: plain ID → IRI
        if (referenceFields[key] && value !== null && value !== undefined && value !== '') {
            if (!String(value).startsWith('/api/')) {
                denormalized[key] = idToIri(referenceFields[key], value);
            }
        }
        // Arrays
        else if (Array.isArray(value)) {
            let targetResource: string | undefined = undefined;
            if (key === 'userRoles') {
                targetResource = 'roles';
            } else if (key === 'userGroups') {
                targetResource = 'user_groups';
            } else {
                const singularKey = key.replace(/s$/, '');
                targetResource = referenceFields[singularKey];
            }

            if (targetResource) {
                // Array of IRI references
                denormalized[key] = value.map((item: any) => {
                    if (typeof item === 'object' && item?.['@id']) return item['@id'];
                    if (String(item).startsWith('/api/')) return item;
                    return idToIri(targetResource!, item);
                });
            } else {
                // Array of nested objects (e.g. reminders) → recurse
                denormalized[key] = value.map((item: any) => {
                    if (item && typeof item === 'object' && !item['@id']) {
                        return denormalizeData(resource, item);
                    }
                    return item;
                });
            }
        }
    }

    // Reduce reminders to their writable fields only. Handled here rather than in the
    // generic array branch, which would stringify the nested emailTemplate reference
    // to "[object Object]". Server-managed fields (id, scheduledFor, isSent, sentAt)
    // are dropped; the collection is replaced wholesale on save.
    if (Array.isArray(denormalized.reminders)) {
        denormalized.reminders = denormalized.reminders.map((item: any) => {
            if (!item || typeof item !== 'object') return item;
            const days = item.daysBeforeDue;
            return {
                daysBeforeDue:
                    days === undefined || days === null || days === '' ? days : Number(days),
                recipientType: item.recipientType,
                emailTemplate: refToIri('document_templates', item.emailTemplate),
            };
        });
    }

    return denormalized;
};

const dataProvider: DataProvider = {
    getList: (resource, params) => {
        const page = params.pagination?.page || 1;
        const perPage = params.pagination?.perPage || 10;
        const field = params.sort?.field || 'id';
        const order = params.sort?.order || 'ASC';

        const query: Record<string, string> = {
            [`order[${field}]`]: order,
            page: page.toString(),
            itemsPerPage: perPage.toString(),
        };

        // Add filter parameters.
        //
        // Issue #162: <ReferenceInput source="course.id"> + react-hook-form
        // schreiben den Filter als nested Object — { course: { id: 5 } } —
        // nicht als flachen "course.id"-Key. Vorher hat String({id:5}) zu
        // "[object Object]" aufgeloest und der Filter war effektiv weg.
        // Wir entpacken hier auf dot-notation (`course.id=5`), genau das
        // Format das API-Platforms SearchFilter fuer Relation-Searches
        // erwartet.
        if (params.filter) {
            Object.keys(params.filter).forEach(key => {
                const val = params.filter[key];
                if (val === undefined || val === null || val === '') return;
                if (typeof val === 'object' && !Array.isArray(val)) {
                    // Nested object: flatten one level via dot-notation. Wir
                    // ueberspringen leere/leerstring/null-Sub-Werte, sonst
                    // landet ein leerer Filter wieder als "[object Object]".
                    Object.keys(val).forEach(subKey => {
                        const subVal = (val as Record<string, unknown>)[subKey];
                        if (subVal === undefined || subVal === null || subVal === '') return;
                        query[`${key}.${subKey}`] = String(subVal);
                    });
                } else {
                    query[key] = String(val);
                }
            });
        }

        const url = `${getApiUrl(resource)}/${resource}?${new URLSearchParams(query).toString()}`;

        return httpClient(url).then(({ json }) => {
            const members = json['hydra:member'] || json.member || [];
            return {
                data: members.map(normalizeData),
                total: json['hydra:totalItems'] || json.totalItems || 0,
            };
        });
    },

    getOne: (resource, params) =>
        httpClient(`${getApiUrl(resource)}/${resource}/${params.id}`).then(({ json }) => ({
            data: normalizeData(json),
        })),

    getMany: (resource, params) => {
        const query = params.ids.map((id: any) => `id[]=${id}`).join('&');
        const url = `${getApiUrl(resource)}/${resource}?${query}&itemsPerPage=${params.ids.length}`;
        return httpClient(url).then(({ json }) => {
            const members = json['hydra:member'] || json.member || [];
            return {
                data: members.map(normalizeData),
            };
        });
    },

    getManyReference: (resource, params) => {
        const page = params.pagination?.page || 1;
        const perPage = params.pagination?.perPage || 10;
        const field = params.sort?.field || 'id';
        const order = params.sort?.order || 'ASC';

        const query: Record<string, string> = {
            [params.target]: String(params.id),
            [`order[${field}]`]: order,
            page: page.toString(),
            itemsPerPage: perPage.toString(),
        };

        const url = `${getApiUrl(resource)}/${resource}?${new URLSearchParams(query).toString()}`;

        return httpClient(url).then(({ json }) => {
            const members = json['hydra:member'] || json.member || [];
            return {
                data: members.map(normalizeData),
                total: json['hydra:totalItems'] || json.totalItems || 0,
            };
        });
    },

    create: (resource, params) => {
        const data = denormalizeData(resource, params.data);
        return httpClient(`${getApiUrl(resource)}/${resource}`, {
            method: 'POST',
            body: JSON.stringify(data),
        }).then(({ json }) => ({
            data: normalizeData(json),
        }));
    },

    update: (resource, params) => {
        const data = denormalizeData(resource, params.data);
        return httpClient(`${getApiUrl(resource)}/${resource}/${params.id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }).then(({ json }) => ({
            data: normalizeData(json),
        }));
    },

    updateMany: (resource, params) => {
        const data = denormalizeData(resource, params.data);
        return Promise.all(
            params.ids.map(id =>
                httpClient(`${getApiUrl(resource)}/${resource}/${id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(data),
                })
            )
        ).then(responses => ({ data: responses.map(({ json }) => json.id) }));
    },

    delete: (resource, params) =>
        httpClient(`${getApiUrl(resource)}/${resource}/${params.id}`, {
            method: 'DELETE',
        }).then(({ json }) => ({ data: normalizeData(json) })),

    deleteMany: (resource, params) => {
        return Promise.all(
            params.ids.map(id =>
                httpClient(`${getApiUrl(resource)}/${resource}/${id}`, {
                    method: 'DELETE',
                })
            )
        ).then(responses => ({ data: params.ids }));
    },
};

export default dataProvider;
